import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CmInterceptHeaders } from '@ats/cm/core';
import { take } from 'rxjs/operators';
import { LogLevel } from './log-level.enum';
import { LoggingConfig } from './logging-config.interface';
import { LOG_CONFIG } from './logging-config.token';

@Injectable({
	providedIn: 'root',
})
export class LoggingService {
	logLevel = LogLevel.Trace;
	headerStyle = 'font-size: 10px; font-weight: normal; text-transform: uppercase;';

	constructor(
		@Inject(LOG_CONFIG) private readonly config: LoggingConfig,
		private http: HttpClient
	) {
		if (!config || !config.logApi) {
			throw new Error('Logging service requires a configuration');
		}

		if (config.logLevel) {
			this.logLevel = config.logLevel;
		}
	}

	logToApi(error: any) {
		this.sendToApi(error);
	}

	logToApiWithHeaders(error: any, headers: HttpHeaders) {
		this.sendToApi(error, headers);
	}

	trace(...args: any[]) {
		if (this.logLevel <= LogLevel.Trace) {
			this.logToConsole('#6df417', 'Trace: ', ...args);
		}
	}

	debug(...args: any[]) {
		if (this.logLevel <= LogLevel.Debug) {
			this.logToConsole('#f417e0', 'Debug: ', ...args);
		}
	}

	info(...args: any[]) {
		if (this.logLevel <= LogLevel.Information) {
			this.logToConsole('#f9fb14', 'Info: ', ...args);
		}
	}

	warn(...args: any[]) {
		if (this.logLevel <= LogLevel.Warning) {
			this.logToConsole('#fbce14', 'Warn: ', ...args);
		}
	}

	error(...args: any[]) {
		if (this.logLevel <= LogLevel.Error) {
			this.logToConsole('#fb3714', 'Error: ', ...args);
		}
	}

	critical(...args: any[]) {
		if (this.logLevel <= LogLevel.Critical) {
			this.logToConsole('#fb1471', 'Critical: ', ...args);
		}
	}

	private logToConsole(color: string, header: string, ...args: any[]) {
		const message = typeof args[0] === 'string' ? args.shift() : '';
		if (args.length) {
			console.group(
				`%c${header}%c${message}`,
				`color: ${color};${this.headerStyle}`,
				`font-size: 14px; color: ${color};`
			);
			args.forEach(arg => {
				if (typeof arg === 'string') {
					console.log(`%c ${arg}`, `color: ${color};`);
				} else {
					console.log(arg);
				}
			});
			console.groupCollapsed(`%cTrace`, `color: #666;${this.headerStyle}`);
			console.trace();
			console.groupEnd();
			console.groupEnd();
		} else {
			console.groupCollapsed(
				`%c${header}%c${message}`,
				`color: ${color};${this.headerStyle}`,
				`font-size: 14px; color: ${color};`
			);
			console.trace();
			console.groupEnd();
		}
	}

	private sendToApi(error: any, headers?: HttpHeaders): void {
		const { message, errors } = error;
		this.http
			.post(this.config.logApi, error, {
				headers,
			})
			.pipe(take(1))
			.subscribe(() => {
				if (headers && this.hasSuppressHeaders(headers, error)) {
					return;
				}
				if (errors) {
					errors.forEach((error: Error) => this.error(error.message || '', error));
				} else {
					this.error(message, error.origin || error);
				}
			});
	}

	hasSuppressHeaders(headers: HttpHeaders, error: any): boolean {
		const suppressError = headers.has(CmInterceptHeaders.Type.SuppressError);
		const hasSuppressHeader = headers.has(CmInterceptHeaders.Type.SuppressErrorLog);
		const suppressErrorId = headers.get(CmInterceptHeaders.Type.SuppressErrorById);
		const hasSuppressErrorId = suppressErrorId
			? suppressErrorId === error?.id?.toString()
			: false;
		return suppressError || hasSuppressHeader || hasSuppressErrorId;
	}
}
